import React, { createElement } from 'react'
import { Missing, ErrorBoundary, Warnings, Graceful } from './tooling-components'
import FixTagRule from './rules/FixTagRule'
import { hasTag } from './utils/tagsUtils'
import NbspRule from './rules/NbspRule'
import NonBreakingHyphenRule from './rules/NonBreakingHyphenRule'
import PicturesAndMediaRule from './rules/PicturesAndMediaRule'
import AdditionalCTARule from './rules/AdditionalCTARule'
import UserAttributesCollectionRule from './rules/UserAttributesCollectionRule'
import lazyLoadingRule from './rules/lazyLoadingRule'
import CMV2Rule from './rules/CMV2Rule'

const WEBVIEW_HIDDEN_TAG = 'Webview.Hidden'
/* This flag is used to indicate that a component should not be rendered, like when data will be
extracted and manually rendered. */
const DISABLE_RENDERING_FLAG = 'disableRendering'

export const createComponentMap = (components) => {
  return components.reduce((acc, item) => {
    if (!item.layoutVariant) {
      return acc
    }
    const variants = [].concat(item.layoutVariant).reduce((acc, variant) => ({ ...acc, [variant]: item }), {})
    return { ...acc, ...variants }
  }, {})
}

export const withLayoutVariant = (layoutVariant, render) => {
  render.layoutVariant = layoutVariant
  return render
}

export const withRootPosition = (rootNode, positionOption = {} ) => {
  const { ignorePositioning = ['placeholder'] } = positionOption
  return rootNode.map((n) => {
    if(!n.items) {
      return {
        ...n,
        rootPosition: 1
      }
    }
    let rootPosition = 0
    const items = n.items.map((item) => {
      if(!ignorePositioning.includes(item.type)) {
        rootPosition += 1
        return {
          ...item,
          rootPosition
        }
      }
      return item
    })
    return {
      ...n,
      items
    }
  })
}
//temp check item type as cm return array of strings
export const pageVisitor = (node, fn, parent = null, index = 0, positionOption) => {
    const childrenNodes = node.items || node.articleSubElements || node.stickyTeaser
    let childrenElements = null
    const childMapper = (item, index) => {
      if (node.rootPosition && typeof item === 'object') {
        item.rootPosition = node.rootPosition
     }
      if (!item.position) return pageVisitor(item, fn, node, index)

      const itemWithPosition = {
        ...item.item,
        position: item.position
      }
      return pageVisitor(itemWithPosition, fn, node, index)
    }
    if (node.pageKey || node.contentKey) {
      childrenElements = Object.keys(node).reduce((acc, key) => {
        const isAllowed = ['css', 'javascript', 'tags', '_metadata'].indexOf(key) === -1
        if (isAllowed && Array.isArray(node[key])) {
          const nodeWithRootPosition = withRootPosition(node[key], positionOption)
          acc[key] = nodeWithRootPosition.map(childMapper)
        }
        return acc
      }, {})
    } else if (childrenNodes) {
      childrenElements = childrenNodes.map(childMapper)
    }
    return fn(node, parent, childrenElements, index)
  }

  function checkPropTypes(typeSpecs, values, location, componentName) {
    return Object.keys(typeSpecs).reduce((acc, typeSpecName) => {
      const error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED')
      if (error instanceof Error) {
        return acc.concat(typeSpecName)
      }
      return acc
    }, [])
  }

  const pick = (o, props) => Object.assign({}, ...props.map((prop) => ({ [prop]: o[prop] })))

  export const processPageMetdata = (coremediaPage) => {
    if (coremediaPage && !!coremediaPage.pageKey) {
      if (Array.isArray(coremediaPage._metadata)) {
        const linkedPage = coremediaPage._metadata[0]
        if (linkedPage && linkedPage.type === 'page') {
          const pageProps = pick(linkedPage, ['pageURL', 'seoSegment', 'seoHTMLTitle', 'seoHTMLMetaDescription', 'keywords', 'tags'])
          return {
            ...coremediaPage,
            ...pageProps
          }
        }
      }
    }
    return coremediaPage
  }

  export const CoremediaRenderer = (coremediaPage, components, options = {}) => {
    const { useDebugRenderingTools = false, index, position = {} } = options
    const { lazyLoadingPosition = 4 } = position
    let coremediaPageMeta = processPageMetdata(coremediaPage)

    // if article, flat 'content' field to object root
    if (coremediaPageMeta.contentKey) {
      coremediaPageMeta = {
        ...coremediaPageMeta,
        ...(coremediaPageMeta.content ? coremediaPageMeta.content[0] : {})
      }
      delete coremediaPageMeta.content
    }

    options.rules = [CMV2Rule, PicturesAndMediaRule, FixTagRule, NbspRule, NonBreakingHyphenRule, AdditionalCTARule, UserAttributesCollectionRule, lazyLoadingRule].concat(options.rules || [])
    const ToolingComponents = {
      Graceful,
      Warnings,
      ErrorBoundary,
      Missing,
      ...(options.ToolingComponents || {})
    }
    const componentMap = createComponentMap(components)
    const rootNode = pageVisitor(
      coremediaPageMeta,
      (item, parent, childrenElements, index) => {
        if ((item.type === 'page' && !item.pageKey) || (item.type === 'article' && !item.contentKey)) {
          delete item.layoutVariant
        }

        if (!item.layoutVariant && parent && typeof item ==='object') {
          item.layoutVariant = `${parent.layoutVariant}:child`
        }

        const Component = componentMap[item.layoutVariant]
        const key = `${item.layoutVariant}_${index}`

        const page = coremediaPageMeta.pageKey || coremediaPageMeta.contentKey ? coremediaPageMeta : null
        const additionalProps =
          options.rules &&
          options.rules.reduce((accumulator, currentValue) => {
            const additionPropsFromRule = currentValue({ item, parent, index, page, lazyLoadingPosition, ...accumulator }) || {}
            return Object.assign(accumulator, additionPropsFromRule)
          }, {})

        if (additionalProps[DISABLE_RENDERING_FLAG]) {
          return null
        }

        if (Component) {
          let warnings = []
          if (Component.itemPropTypes) {
            warnings = checkPropTypes(Component.itemPropTypes, item, 'props', item.layoutVariant)
          }

          const isComponentHidden = additionalProps.webview && hasTag(item.tags, WEBVIEW_HIDDEN_TAG)

          const componentProps = {
            item,
            parent,
            index,
            page,
            ...additionalProps,
            [item.pageKey || item.contentKey ? 'areas' : 'children']: childrenElements
          }

          const Handler = useDebugRenderingTools ? ToolingComponents.ErrorBoundary : ToolingComponents.Graceful
          const comp = (
            <Handler key={key} item={item}>
              {isComponentHidden ? null : createElement(Component, componentProps)}
            </Handler>
          )

          if (useDebugRenderingTools && warnings.length > 0) {
            return (
              <ToolingComponents.Warnings key={`warnings_${key}`} item={item} warnings={warnings}>
                {comp}
              </ToolingComponents.Warnings>
            )
          }

          return comp
        }

        const Handler = useDebugRenderingTools ? ToolingComponents.Missing : ToolingComponents.Graceful
        return (
          <Handler key={`missing_${key}`} item={item}>
            {childrenElements && (Array.isArray(childrenElements) ? childrenElements : Object.values(childrenElements))}
          </Handler>
        )
      },
      null,
      index,
      position
    )

    return rootNode
  }
